import { useCallback, useEffect, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import { hasInventoryCountPermission } from "../../utils/purchasingStockPermissions";

/** Keep an open count tied to the account and hotel that opened it. */
export default function useInventoryCountAccess(businessUnitId) {
  const [user, setUser] = useState(readStoredUser);
  const expectedUserId = useRef(user?.id);
  const contextMatches = useCallback((candidate) => Boolean(candidate?.id) &&
    String(candidate.id) === String(expectedUserId.current) &&
    Number.isSafeInteger(Number(businessUnitId)) && Number(businessUnitId) > 0 &&
    Number(candidate.active_business_unit_id) === Number(businessUnitId), [businessUnitId]);
  useEffect(() => {
    const update = () => {
      const candidate = readStoredUser();
      setUser((current) => JSON.stringify(current) === JSON.stringify(candidate) ? current : candidate);
    };
    update();
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, [businessUnitId]);
  const can = useCallback((action, current = false) => {
    const candidate = current ? readStoredUser() : user;
    return contextMatches(candidate) && hasInventoryCountPermission(candidate, "view") &&
      (action === "view" || hasInventoryCountPermission(candidate, action));
  }, [user, contextMatches]);
  const requestConfig = useCallback((action = "view") => {
    if (!can(action, true)) throw new Error("Inventory count access or the active session changed. Reopen the count from the current hotel.");
    return { headers: { "X-Pilcore-Business-Unit-Id": businessUnitId }, pilcoreExpectedUserId: expectedUserId.current };
  }, [businessUnitId, can]);
  return { can, requestConfig, contextKey: JSON.stringify([user?.id, user?.active_business_unit_id, user?.permissions, businessUnitId]) };
}
